import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'gatsby'
import Validate from './validation.js'
import FieldErrorMessage from './field_error_message.js'
import ReCAPTCHA from 'react-google-recaptcha'
import EquipmentItemFields from './equipment_item_fields.js'
import HireDateField from './hire_date_field.js'
import {
  HIRE_LOCATIONS,
  buildCategoryOptions,
  buildProductsByCategory,
  isCatalogItemValid,
  prefillItemFromEquipment,
} from '../data/booking_catalog.js'
import { isValidDdMmYyyy, isDdMmYyyyOnOrAfterTomorrow, parseDdMmYyyy, startOfTomorrowLocal } from '../data/date_format.js'
import { BOOKING_SUBMISSION_KEY, goToFormConfirmation } from '../data/form_storage.js'
import {
  RECAPTCHA_SITE_KEY as SITE_KEY,
  getRecaptchaToken,
  recaptchaErrorMessage,
  shouldSkipRecaptchaClient,
} from '../utils/recaptcha.js'

const emptyItem = () => ({
  category: 'forklifts',
  type: '',
  model: '',
  qty: '1',
  start_hire: '',
  end_hire: '',
  delivery_address: '',
  delivery_contact_name: '',
  delivery_contact_number: '',
  preferred_delivery_time: '',
})

const emptyControls = () => ({
  full_name: { value: '', valid: false, touched: false, validationRules: { isRequired: true }, errorMessages: '' },
  company_name: { value: '', valid: false, touched: false, validationRules: { isRequired: true }, errorMessages: '' },
  email: { value: '', valid: false, touched: false, validationRules: { isRequired: true, isEmail: true }, errorMessages: '' },
  phone: { value: '', valid: false, touched: false, validationRules: { isRequired: true }, errorMessages: '' },
  contact_method: { value: '', valid: false, touched: false, validationRules: { isRequired: true }, errorMessages: '' },
  po_number: { value: '', valid: false, touched: false, validationRules: { isRequired: true }, errorMessages: '' },
  hire_location: { value: '', valid: false, touched: false, validationRules: { isRequired: true }, errorMessages: '' },
  notes: { value: '', valid: true, touched: false, validationRules: {}, errorMessages: '' },
  terms: { value: '', valid: false, touched: false, validationRules: { isRequired: true }, errorMessages: '' },
  type_name: { value: '', valid: false, touched: false, validationRules: { isRequired: true }, errorMessages: '' },
})

const itemDeliveryFilled = (item) =>
  Boolean(
    isValidDdMmYyyy(item.start_hire) &&
      isDdMmYyyyOnOrAfterTomorrow(item.start_hire) &&
      isValidDdMmYyyy(item.end_hire) &&
      item.preferred_delivery_time &&
      item.delivery_address &&
      item.delivery_contact_name &&
      item.delivery_contact_number
  )

const areItemsValid = (items, catalog) =>
  items.every((item) => isCatalogItemValid(item, catalog) && itemDeliveryFilled(item))

const areControlsValid = (controls) =>
  Object.values(controls).every((control) => control.valid)

export default function BookingForm() {
  const recaptchaRef = useRef(null)
  const formErrorRef = useRef(null)
  const [items, setItems] = useState([emptyItem()])
  const [itemsError, setItemsError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [clientReady, setClientReady] = useState(false)
  const [recaptchaReady, setRecaptchaReady] = useState(() => shouldSkipRecaptchaClient() || !SITE_KEY)
  const [formValues, setFormValues] = useState({
    formErrors: '',
    formControls: emptyControls(),
  })

  const categoryOptions = useMemo(() => buildCategoryOptions(), [])
  const productsByCategory = useMemo(() => buildProductsByCategory(), [])

  useEffect(() => {
    setClientReady(true)
  }, [])

  const appliedPrefill = useRef(false)
  useEffect(() => {
    if (appliedPrefill.current || typeof window === 'undefined') return
    const equipment = new URLSearchParams(window.location.search).get('equipment') || ''
    const prefill = prefillItemFromEquipment(equipment)
    if (!prefill) return
    appliedPrefill.current = true
    setItems((prev) =>
      prev.map((item, i) => (i === 0 ? { ...item, ...prefill } : item))
    )
  }, [])

  const formIsValid = useMemo(
    () =>
      areControlsValid(formValues.formControls) &&
      areItemsValid(items, productsByCategory),
    [formValues.formControls, items, productsByCategory]
  )

  const showFormError = (message) => {
    setFormValues((prev) => ({ ...prev, formErrors: message }))
    requestAnimationFrame(() => {
      formErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    })
  }

  const changeHandler = (e) => {
    const { name, value, type, checked } = e.target
    setFormValues((prev) => {
      const updatedControls = { ...prev.formControls }
      const updatedFormElement = { ...updatedControls[name] }

      if (name === 'terms') {
        updatedFormElement.value = checked ? 'yes' : ''
      } else if (type === 'radio') {
        updatedFormElement.value = value
      } else {
        updatedFormElement.value = value
      }

      updatedFormElement.touched = true
      const validateFormElement = Validate(updatedFormElement.value, updatedFormElement.validationRules)
      updatedFormElement.valid = validateFormElement.isValid
      updatedFormElement.errorMessages = validateFormElement.errorMessages
      updatedControls[name] = updatedFormElement

      return {
        ...prev,
        formControls: updatedControls,
        formErrors: '',
      }
    })
  }

  const updateItem = (index, field, value) => {
    setItemsError('')
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item
        const updated = { ...item, [field]: value }
        if (field === 'category') {
          updated.type = ''
          updated.model = ''
          updated.qty = '1'
        }
        if (field === 'type') {
          updated.model = ''
          updated.qty = '1'
        }
        return updated
      })
    )
  }

  const addItem = () => {
    setItems((prev) => {
      const source = [...prev].reverse().find(itemDeliveryFilled) || prev[0] || emptyItem()
      return [
        ...prev,
        {
          ...emptyItem(),
          start_hire: source.start_hire || '',
          end_hire: source.end_hire || '',
          delivery_address: source.delivery_address || '',
          delivery_contact_name: source.delivery_contact_name || '',
          delivery_contact_number: source.delivery_contact_number || '',
        },
      ]
    })
  }

  const touchAndValidateControls = (controls) => {
    const updated = {}
    for (const name of Object.keys(controls)) {
      const el = { ...controls[name], touched: true }
      const result = Validate(el.value, el.validationRules)
      el.valid = result.isValid
      el.errorMessages = result.errorMessages
      updated[name] = el
    }
    return updated
  }

  const submitHandler = async (e) => {
    e.preventDefault()
    if (submitting) return

    const updatedControls = touchAndValidateControls(formValues.formControls)
    const controlsOk = areControlsValid(updatedControls)
    const itemsOk = areItemsValid(items, productsByCategory)

    setFormValues((prev) => ({
      ...prev,
      formControls: updatedControls,
      formErrors: '',
    }))
    setItemsError(
      itemsOk
        ? ''
        : 'Please complete category, type, size, hire dates (DD/MM/YYYY) and delivery for each item.'
    )

    if (!controlsOk || !itemsOk) {
      setFormValues((prev) => ({
        ...prev,
        formControls: updatedControls,
        formErrors: 'Please complete all required fields marked with *.',
      }))
      requestAnimationFrame(() => {
        formErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      })
      return
    }

    let recaptchaToken = 'dev-bypass'
    try {
      recaptchaToken = await getRecaptchaToken(recaptchaRef)
    } catch (err) {
      showFormError(recaptchaErrorMessage(err))
      return
    }

    setSubmitting(true)

    try {
      const fieldData = new FormData()
      for (let formElementId in updatedControls) {
        fieldData.append(formElementId, updatedControls[formElementId].value)
      }

      const first = items[0] || emptyItem()
      fieldData.append('start_hire', first.start_hire)
      fieldData.append('end_hire', first.end_hire)
      fieldData.append('delivery_address', first.delivery_address)
      fieldData.append('delivery_contact_name', first.delivery_contact_name)
      fieldData.append('delivery_contact_number', first.delivery_contact_number)
      fieldData.append('items', JSON.stringify(items))
      fieldData.append('recaptcha', recaptchaToken)

      const res = await fetch('/api/booking', {
        method: 'POST',
        body: fieldData,
      })

      if (res.status === 200) {
        const summary = {
          full_name: updatedControls.full_name.value,
          company_name: updatedControls.company_name.value,
          email: updatedControls.email.value,
          phone: updatedControls.phone.value,
          contact_method: updatedControls.contact_method.value,
          po_number: updatedControls.po_number.value,
          hire_location: updatedControls.hire_location.value,
          notes: updatedControls.notes.value,
          terms: updatedControls.terms.value,
          type_name: updatedControls.type_name.value,
          items,
        }
        goToFormConfirmation('/booking-confirmation/', BOOKING_SUBMISSION_KEY, summary)
        return
      }

      const errors = await res.json().catch(() => ({ message: 'Something went wrong, please try again.' }))
      showFormError(errors.message || 'Something went wrong, please try again.')
    } catch (err) {
      showFormError('Something went wrong, please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const c = formValues.formControls

  const renderItemFields = (item, index) => {
    const tomorrow = startOfTomorrowLocal()
    const startDate = parseDdMmYyyy(item.start_hire)
    const endMinDate = startDate && startDate.getTime() > tomorrow.getTime() ? startDate : tomorrow
    return (
    <EquipmentItemFields
      key={index}
      item={item}
      index={index}
      categoryOptions={categoryOptions}
      productsByCategory={productsByCategory}
      updateItem={updateItem}
      showForkliftSizeHelp
    >
      {index === 0 ? (
        <>
          <label className="hire_field">
            <span className="hire_label">PO Number <span className="req">*</span></span>
            <input type="text" name="po_number" value={c.po_number.value} onChange={changeHandler} required />
          </label>
          <FieldErrorMessage className={c.po_number.touched && !c.po_number.valid ? 'show field__errors' : 'hide field__errors'} errorMessages={c.po_number.errorMessages} />
        </>
      ) : null}

      <div className="hire_field">
        <span className="hire_label">Start of Hire / Rental <span className="req">*</span></span>
        <HireDateField
          id={`start_hire_${index}`}
          value={item.start_hire}
          onChange={(value) => updateItem(index, 'start_hire', value)}
          ariaLabel="Start of Hire / Rental (DD/MM/YYYY)"
          minDate={tomorrow}
        />
      </div>

      <div className="hire_field">
        <span className="hire_label">End of Hire / Rental <span className="req">*</span></span>
        <HireDateField
          id={`end_hire_${index}`}
          value={item.end_hire}
          onChange={(value) => updateItem(index, 'end_hire', value)}
          ariaLabel="End of Hire / Rental (DD/MM/YYYY)"
          minDate={endMinDate}
        />
      </div>

      <label className="hire_field">
        <span className="hire_label">Preferred Delivery Time <span className="req">*</span></span>
        <select
          value={item.preferred_delivery_time || ''}
          onChange={(e) => updateItem(index, 'preferred_delivery_time', e.target.value)}
          required
        >
          <option value="" disabled>Select 7am to 4pm</option>
          {['7:00am','8:00am','9:00am','10:00am','11:00am','12:00pm','1:00pm','2:00pm','3:00pm','4:00pm'].map((slot) => (
            <option key={slot} value={slot}>{slot}</option>
          ))}
        </select>
      </label>
      <p className="hire_field_hint">Our Hire Office will call you on the contact number you have supplied to confirm the date and your preferred delivery time.</p>

      <label className="hire_field">
        <span className="hire_label">Delivery Address <span className="req">*</span></span>
        <input
          type="text"
          value={item.delivery_address}
          onChange={(e) => updateItem(index, 'delivery_address', e.target.value)}
        />
      </label>

      <label className="hire_field">
        <span className="hire_label">Delivery Contact Name <span className="req">*</span></span>
        <input
          type="text"
          value={item.delivery_contact_name}
          onChange={(e) => updateItem(index, 'delivery_contact_name', e.target.value)}
        />
      </label>

      <label className="hire_field">
        <span className="hire_label">Delivery Contact Number <span className="req">*</span></span>
        <input
          type="tel"
          value={item.delivery_contact_number}
          onChange={(e) => updateItem(index, 'delivery_contact_number', e.target.value)}
        />
      </label>
    </EquipmentItemFields>
    )
  }

  return (
    <form className="hire_form" name="booking" onSubmit={submitHandler} noValidate>
      <div className="hire_form_banner">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10z" />
        </svg>
        <span>Book Online</span>
      </div>

      <label className="hire_field">
        <span className="hire_label">Full Name <span className="req">*</span></span>
        <input type="text" name="full_name" value={c.full_name.value} onChange={changeHandler} />
      </label>
      <FieldErrorMessage className={c.full_name.touched && !c.full_name.valid ? 'show field__errors' : 'hide field__errors'} errorMessages={c.full_name.errorMessages} />

      <label className="hire_field">
        <span className="hire_label">Company Name <span className="req">*</span></span>
        <input type="text" name="company_name" value={c.company_name.value} onChange={changeHandler} />
      </label>
      <FieldErrorMessage className={c.company_name.touched && !c.company_name.valid ? 'show field__errors' : 'hide field__errors'} errorMessages={c.company_name.errorMessages} />

      <label className="hire_field">
        <span className="hire_label">Email Address <span className="req">*</span></span>
        <input type="email" name="email" value={c.email.value} onChange={changeHandler} />
      </label>
      <FieldErrorMessage className={c.email.touched && !c.email.valid ? 'show field__errors' : 'hide field__errors'} errorMessages={c.email.errorMessages} />

      <label className="hire_field">
        <span className="hire_label">Contact Number <span className="req">*</span></span>
        <input type="tel" name="phone" value={c.phone.value} onChange={changeHandler} />
      </label>
      <FieldErrorMessage className={c.phone.touched && !c.phone.valid ? 'show field__errors' : 'hide field__errors'} errorMessages={c.phone.errorMessages} />

      <fieldset className="hire_radio_group">
        <legend>Preferred Contact Method <span className="req">*</span></legend>
        <label className="hire_radio">
          <input type="radio" name="contact_method" value="Phone" checked={c.contact_method.value === 'Phone'} onChange={changeHandler} />
          Phone
        </label>
        <label className="hire_radio">
          <input type="radio" name="contact_method" value="Email" checked={c.contact_method.value === 'Email'} onChange={changeHandler} />
          Email
        </label>
      </fieldset>
      <FieldErrorMessage className={c.contact_method.touched && !c.contact_method.valid ? 'show field__errors' : 'hide field__errors'} errorMessages={c.contact_method.errorMessages} />

      <label className="hire_field">
        <span className="hire_label">Hire Location <span className="req">*</span></span>
        <select name="hire_location" value={c.hire_location.value} onChange={changeHandler}>
          <option value="" disabled>Select Auckland, Waikato or Christchurch</option>
          {HIRE_LOCATIONS.map((loc) => (
            <option key={loc} value={loc}>{loc}</option>
          ))}
        </select>
      </label>
      <FieldErrorMessage className={c.hire_location.touched && !c.hire_location.valid ? 'show field__errors' : 'hide field__errors'} errorMessages={c.hire_location.errorMessages} />

      {items.map((item, index) => renderItemFields(item, index))}
      {itemsError ? <p className="hire_form_error">{itemsError}</p> : null}

      <button type="button" className="hire_add_item" onClick={addItem}>
        ADD ANOTHER ITEM FOR HIRE
      </button>

      <label className="hire_field">
        <span className="hire_label">Notes</span>
        <textarea name="notes" rows="4" value={c.notes.value} onChange={changeHandler} />
      </label>

      <label className="hire_terms">
        <input type="checkbox" name="terms" checked={c.terms.value === 'yes'} onChange={changeHandler} />
        <span>
          <span className="req">*</span>{' '}
          I agree to Forklift Solutions{' '}
          <Link to="/terms/" target="_blank" rel="noopener noreferrer">Terms and Conditions of Hire</Link>
        </span>
      </label>
      <FieldErrorMessage className={c.terms.touched && !c.terms.valid ? 'show field__errors' : 'hide field__errors'} errorMessages={c.terms.errorMessages} />

      <label className="hire_field">
        <span className="hire_label">Type Name <span className="req">*</span></span>
        <input type="text" name="type_name" value={c.type_name.value} onChange={changeHandler} />
      </label>
      <FieldErrorMessage className={c.type_name.touched && !c.type_name.valid ? 'show field__errors' : 'hide field__errors'} errorMessages={c.type_name.errorMessages} />

      {SITE_KEY && clientReady && !shouldSkipRecaptchaClient() ? (
        <ReCAPTCHA
          size="invisible"
          ref={recaptchaRef}
          sitekey={SITE_KEY}
          asyncScriptOnLoad={() => setRecaptchaReady(true)}
        />
      ) : null}

      {formValues.formErrors ? (
        <p className="hire_form_error" ref={formErrorRef}>{formValues.formErrors}</p>
      ) : null}

      <button
        type="submit"
        disabled={submitting}
        className={`hire_submit${formIsValid ? '' : ' hire_submit_incomplete'}`}
      >
        {submitting ? 'Submitting…' : 'Submit'}
      </button>
    </form>
  )
}
